import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Link,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  CheckCircle,
  Medication,
  Psychology,
  Message,
  HealthAndSafety
} from '@mui/icons-material';

// Import logo and illustrations
import avenLogo from '../assets/images/aven-logo.svg';
import heroImage from '../assets/images/hero-image.svg';
import featureImage1 from '../assets/images/feature-1.svg';
import featureImage2 from '../assets/images/feature-2.svg';

const Home = () => {
  return (
    <>
      {/* Header/Navigation */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <img src={avenLogo} alt="Aven Health Logo" height={40} />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button color="inherit" component={RouterLink} to="/login">
              Sign In
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              component={RouterLink} 
              to="/register"
            >
              Sign Up
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Hero Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(to right, #f8f9fa, #e8f5e9)',
          pt: 10,
          pb: 6
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  color: 'primary.main'
                }}
              >
                Your AI Health Companion for GLP-1 Medications
              </Typography>
              
              <Typography variant="h6" color="textSecondary" paragraph sx={{ mb: 4 }}>
                Aven helps you manage your GLP-1 medication journey with personalized reminders, 
                side effect tracking, and AI-powered coaching.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  component={RouterLink}
                  to="/register"
                  sx={{ px: 4, py: 1.5 }}
                >
                  Get Started
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary"
                  size="large"
                  component={RouterLink}
                  to="/learn-more"
                  sx={{ px: 4, py: 1.5 }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img 
                  src={heroImage} 
                  alt="Aven Health App" 
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto',
                    borderRadius: '12px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Features Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom>
              Features Designed for Your Health Journey
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Aven provides comprehensive support for your GLP-1 medication management
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Medication color="primary" fontSize="large" sx={{ mr: 1 }} />
                    <Typography variant="h5" component="h3">
                      Medication Reminders
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="textSecondary">
                    Never miss a dose with customized reminders and adherence tracking.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Personalized schedules
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        WhatsApp notifications
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Adherence tracking
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Psychology color="primary" fontSize="large" sx={{ mr: 1 }} />
                    <Typography variant="h5" component="h3">
                      AI Coaching
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="textSecondary">
                    Receive personalized guidance and support from our AI health coach.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Cognitive behavioral techniques
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Goal setting and tracking
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Personalized health insights
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <HealthAndSafety color="primary" fontSize="large" sx={{ mr: 1 }} />
                    <Typography variant="h5" component="h3">
                      Side Effect Management
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="textSecondary">
                    Track and manage side effects with expert guidance and support.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Symptom tracking
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Management recommendations
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Progress monitoring
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* How It Works Section */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom>
              How Aven Works
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              A simple process to get you started on your health journey
            </Typography>
          </Box>
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <img 
                src={featureImage1} 
                alt="Aven Registration Process" 
                style={{ maxWidth: '100%', height: 'auto' }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  1. Create Your Account
                </Typography>
                <Typography variant="body1" paragraph>
                  Sign up in minutes with our simple registration process. 
                  Enter your basic information and medication details.
                </Typography>
                
                <Typography variant="h4" gutterBottom>
                  2. Set Up Your Medication Schedule
                </Typography>
                <Typography variant="body1" paragraph>
                  Input your medication details and preferred reminder times. 
                  Aven will create a personalized schedule just for you.
                </Typography>
                
                <Typography variant="h4" gutterBottom>
                  3. Connect with Your AI Coach
                </Typography>
                <Typography variant="body1" paragraph>
                  Start chatting with your AI health coach through WhatsApp or the app. 
                  Get personalized guidance and support for your health journey.
                </Typography>
                
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  component={RouterLink}
                  to="/register"
                  sx={{ mt: 2 }}
                >
                  Get Started Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Testimonials Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom>
              What Our Users Say
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Hear from people who have transformed their health journey with Aven
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
                    "Aven has completely transformed how I manage my GLP-1 medication. 
                    The reminders are reliable, and the AI coach provides valuable insights 
                    that have helped me deal with side effects effectively."
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Sarah Johnson
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Using Aven for 6 months
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
                    "The WhatsApp integration is a game-changer. I love being able to 
                    chat with my AI coach anytime, anywhere. It's like having a health 
                    professional in my pocket 24/7."
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Michael Rodriguez
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Using Aven for 3 months
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
                    "As a healthcare provider, I recommend Aven to all my patients on 
                    GLP-1 medications. The adherence tracking and side effect management 
                    features have significantly improved patient outcomes."
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Dr. Emily Chen
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Endocrinologist
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Box 
        sx={{ 
          py: 8, 
          background: 'linear-gradient(45deg, #2e7d32 30%, #43a047 90%)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom>
              Ready to Transform Your Health Journey?
            </Typography>
            <Typography variant="h6" paragraph sx={{ mb: 4 }}>
              Join thousands of users who have improved their medication adherence and health outcomes with Aven.
            </Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              component={RouterLink}
              to="/register"
              sx={{ 
                px: 6, 
                py: 1.5, 
                fontSize: '1.1rem',
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              Sign Up Now
            </Button>
          </Box>
        </Container>
      </Box>
      
      {/* Footer */}
      <Box sx={{ py: 6, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <img src={avenLogo} alt="Aven Health Logo" height={40} />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Aven Health is your AI-powered companion for GLP-1 medication management.
              </Typography>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Company
              </Typography>
              <Link component={RouterLink} to="/about" color="textSecondary" display="block" sx={{ mb: 1 }}>
                About Us
              </Link>
              <Link component={RouterLink} to="/team" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Our Team
              </Link>
              <Link component={RouterLink} to="/careers" color="textSecondary" display="block">
                Careers
              </Link>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Resources
              </Typography>
              <Link component={RouterLink} to="/blog" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Blog
              </Link>
              <Link component={RouterLink} to="/faq" color="textSecondary" display="block" sx={{ mb: 1 }}>
                FAQ
              </Link>
              <Link component={RouterLink} to="/support" color="textSecondary" display="block">
                Support
              </Link>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Legal
              </Typography>
              <Link component={RouterLink} to="/terms" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Terms of Service
              </Link>
              <Link component={RouterLink} to="/privacy" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Privacy Policy
              </Link>
              <Link component={RouterLink} to="/cookies" color="textSecondary" display="block">
                Cookie Policy
              </Link>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Connect
              </Typography>
              <Link href="https://twitter.com/avenhealth" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Twitter
              </Link>
              <Link href="https://facebook.com/avenhealth" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Facebook
              </Link>
              <Link href="https://instagram.com/avenhealth" color="textSecondary" display="block">
                Instagram
              </Link>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #ddd', textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              © {new Date().getFullYear()} Aven Health. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Home;
